import { useMemo } from "react";
import { loadHistory } from "../utils/history";
import { getWeakStats } from "../utils/weakAreas";
import { QUESTION_BANKS, MBTA_SECTIONS, SECTION_META } from "../data/index";

function getSectionReadiness(history, section) {
  const examSessions = history
    .filter(s => s.section === section && s.mode === "exam")
    .slice(0, 5);
  const practiceSessions = history
    .filter(s => s.section === section && s.mode === "practice")
    .slice(0, 3);

  if (examSessions.length === 0 && practiceSessions.length === 0) return null;

  // Weight exams more than practice
  const allScored = [
    ...examSessions.map(s => ({ pct: s.pct, weight: 2 })),
    ...practiceSessions.map(s => ({ pct: s.pct, weight: 1 })),
  ];
  const totalWeight = allScored.reduce((s, x) => s + x.weight, 0);
  const avg = Math.round(allScored.reduce((s, x) => s + x.pct * x.weight, 0) / totalWeight);
  const best = Math.max(...[...examSessions, ...practiceSessions].map(s => s.pct));
  const sessionCount = examSessions.length + practiceSessions.length;

  return { avg, best, sessionCount, examCount: examSessions.length };
}

function ReadinessLevel({ avg }) {
  if (avg >= 85) return { label: "Ready!", color: "#16a34a", bg: "#f0fdf4", border: "#86efac", icon: "✅" };
  if (avg >= 80) return { label: "Almost!", color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: "⚠️" };
  return { label: "Keep Studying", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5", icon: "📚" };
}

export default function ReadinessCard({ onStartWeakDrill }) {
  const { sectionData } = useMemo(() => {
    const history = loadHistory();
    const sectionData = MBTA_SECTIONS.map(section => {
      const meta = SECTION_META[section];
      const readiness = getSectionReadiness(history, section);
      const weakStats = getWeakStats(QUESTION_BANKS[section]);
      return { section, meta, readiness, weakStats };
    });
    return { sectionData };
  }, []);

  const allReady = sectionData.every(d => d.readiness && d.readiness.avg >= 80);
  const anyData = sectionData.some(d => d.readiness !== null);

  if (!anyData) {
    return (
      <div className="readiness-card readiness-empty">
        <div className="readiness-empty-icon">🎯</div>
        <p><strong>Track your readiness!</strong></p>
        <p>Complete a Practice or Exam session and your readiness score will appear here.</p>
      </div>
    );
  }

  return (
    <div className="readiness-card">
      <div className="readiness-header">
        <h3 className="readiness-title">
          {allReady ? "🎉 Exam Ready!" : "📊 Your Readiness"}
        </h3>
        {allReady && (
          <span className="readiness-ready-badge">Ready to test!</span>
        )}
      </div>

      <div className="readiness-sections">
        {sectionData.map(({ section, meta, readiness, weakStats }) => {
          if (!readiness) {
            return (
              <div key={section} className="readiness-section rs-no-data">
                <span className="rs-icon">{meta.icon}</span>
                <div className="rs-info">
                  <span className="rs-name" style={{ color: meta.color }}>{section.replace(" Endorsement", "")}</span>
                  <span className="rs-sub">No sessions yet</span>
                </div>
                <span className="rs-status">—</span>
              </div>
            );
          }

          const level = ReadinessLevel({ avg: readiness.avg });
          return (
            <div
              key={section}
              className="readiness-section"
              style={{ background: level.bg, borderColor: level.border }}
            >
              <span className="rs-icon">{meta.icon}</span>
              <div className="rs-info">
                <span className="rs-name" style={{ color: meta.color }}>{section.replace(" Endorsement", "")}</span>
                <span className="rs-sub">
                  Best: {readiness.best}% &nbsp;·&nbsp; {readiness.sessionCount} session{readiness.sessionCount !== 1 ? "s" : ""}
                  {weakStats.withErrors > 0 && ` · ${weakStats.withErrors} weak Qs`}
                </span>
              </div>
              <div className="rs-score-block">
                <span className="rs-avg" style={{ color: level.color }}>{readiness.avg}%</span>
                <span className="rs-level" style={{ color: level.color }}>{level.icon} {level.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Weak drill CTA */}
      {sectionData.some(d => d.weakStats.withErrors > 0) && (
        <button className="btn btn-weak-drill" onClick={onStartWeakDrill}>
          🎯 Drill Weak Spots ({sectionData.reduce((sum, d) => sum + d.weakStats.withErrors, 0)} questions you've missed)
        </button>
      )}
    </div>
  );
}
